import { useState, useEffect, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import QuantityField from "./quantityfield";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import SelectedItemList from "./selecteditemlist";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CustomNoRowsOverlay from "./customnorowsoverlay";

import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

function ItemList({
  rows,
  searchTerm,
  setSearchTerm,
  selectedColors,
  selectedLines,
  setFetchedData,
  multiplier,
  visibleRows,
  setVisibleRows,
  filterRef,
  fileInputRef,
}) {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery("(max-width: 1200px)");
  const isMediumOrAbove = useMediaQuery(theme.breakpoints.up("md"));
  const isExtraLarge = useMediaQuery(theme.breakpoints.up("xl"));

  const isPhone = !isMediumOrAbove;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    description: false, // default to hidden
  });
  const [showFullItemName, setShowFullItemName] = useState(false);

  useEffect(() => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      description: isExtraLarge, // show only on extra large
    }));
  }, [isExtraLarge]);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      description: !isSmallScreen,
    }));
  }, [isSmallScreen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutside =
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        (!filterRef?.current || !filterRef.current.contains(event.target));

      if (clickedOutside) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterRef]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setDropdownOpen(value.trim() !== "");
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDropdownOpen(false);
  };

  const handleSearchFocus = () => {
    if (searchTerm.trim()) {
      setDropdownOpen(true);
    }
  };

  const handleAddToVisibleRows = (item) => {
    setVisibleRows((prev) => ({
      ...prev,
      [item.id]: item,
    }));
    setSearchTerm("");
    setDropdownOpen(false);
  };

  const handleDeleteRow = (id) => {
    try {
      setVisibleRows((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    } catch (error) {
      console.warn("Delete failed, retrying...", error);

      setTimeout(() => {
        try {
          setVisibleRows((prev) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        } catch (retryError) {
          console.error("Retry failed:", retryError);
        }
      }, 20);
    }
  };

  const deleteLockRef = useRef(false);

  const safelyDeleteRow = (id) => {
    if (deleteLockRef.current) return;

    deleteLockRef.current = true;

    // Use requestAnimationFrame to defer deletion until after rendering and MUI’s lifecycle
    requestAnimationFrame(() => {
      setTimeout(() => {
        setVisibleRows((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });

        deleteLockRef.current = false;
      }, 0); // optional small buffer
    });
  };

  const updateVisibleRow = (id, changes) => {
    setVisibleRows((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...changes,
      },
    }));
  };

  const transformRowForGrid = (item, index) => {
    const msrp = parseFloat(item.msrp?.replace(/[$,]/g, "") || "0");
    const assembly = parseFloat(item.assembly?.replace(/[$,]/g, "") || "0");
    const multiplierValue = parseFloat(multiplier || 1);
    const quantity = item.quantity ?? 1;

    const unitPrice = msrp * multiplierValue;
    const assemblyCost = item.assemblySelected ? assembly * quantity : 0;
    const subtotal = unitPrice * quantity + assemblyCost;

    const assemblyDisplay = item.assemblySelected
      ? item.hinge === "directional" && item.hingeDirection
        ? `Yes (${item.hingeDirection})`
        : "Yes"
      : "";

    return {
      id: item.id || index + 1,
      itemName: {
        product: item.product,
        line: item.line || "",
        color: item.color || "",
      },

      description: item.description.replace(/\\"/g, '"'),
      unitprice: `$${unitPrice.toFixed(2)}`,
      quantity,
      assembly: assemblyDisplay,
      subtotal: `$${subtotal.toFixed(2)}`,
    };
  };

  const dataGridRows = Object.values(visibleRows).map(transformRowForGrid);

  const columns = [
    {
      field: "itemName",
      headerName: "Item",
      flex: 0.3,
      minWidth: showFullItemName ? 240 : 150,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <span>Item</span>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setShowFullItemName((prev) => !prev);
            }}
          >
            {showFullItemName ? (
              <KeyboardArrowLeftIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      ),

      renderCell: (params) => {
        const { product, line, color } = params.value;
        return (
          <span title={`${product}-${line}-${color}`}>
            <strong>{product}</strong>
            {showFullItemName && (
              <>
                {line && ` - ${line}`}
                {color && `-${color}`}
              </>
            )}
          </span>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      sortable: false,
      headerAlign: "center",
      align: "center",
      flex: columnVisibilityModel.description ? 1 : 0,
      minWidth: 150,
      resizable: false,
      renderCell: (params) => (
        <span style={{ fontStyle: "italic" }}>{params.value}</span>
      ),
    },

    {
      field: "unitprice",
      headerName: "Unit Price",
      flex: 0.15,
      sortable: false,
      headerAlign: "center",
      align: "center",
      resizable: false,
      minWidth: 120,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      width: 145,
      sortable: false,
      headerAlign: "center",
      align: "center",
      resizable: false,
      renderCell: (params) => (
        <QuantityField
          value={params.value}
          onChange={(newQty) => {
            setVisibleRows((prev) => ({
              ...prev,
              [params.row.id]: {
                ...prev[params.row.id],
                quantity: newQty,
              },
            }));
          }}
        />
      ),
    },
    {
      field: "assembly",
      headerName: "Assembly",
      flex: 0.2,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      resizable: false,
      renderCell: (params) => {
        const item = visibleRows[params.row.id];
        if (!item) return null;

        return (
          <Checkbox
            sx={{
              color: "black",
              "&.Mui-checked": {
                color: "black",
              },
            }}
            checked={item.assemblySelected || false}
            onChange={(e) =>
              updateVisibleRow(params.row.id, {
                assemblySelected: e.target.checked,
                hingeDirection: e.target.checked ? item.hingeDirection : "", // clear hinge if unchecked
              })
            }
          />
        );
      },
    },
    {
      field: "hinge",
      headerName: "Hinge",
      flex: 0.3,
      minWidth: 120,
      sortable: false,
      headerAlign: "center",
      align: "center",
      resizable: false,
      renderCell: (params) => {
        const item = visibleRows[params.row.id];
        if (!item || !item.assemblySelected || item.hinge !== "directional") {
          return null;
        }

        return (
          <Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={item.hingeDirection || ""}
              onChange={(_, newDir) => {
                if (newDir) {
                  updateVisibleRow(params.row.id, { hingeDirection: newDir });
                }
              }}
            >
              <ToggleButton value="left">Left</ToggleButton>
              <ToggleButton value="right">Right</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        );
      },
    },
    {
      field: "subtotal",
      headerName: "Subtotal",
      flex: 0.2,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      resizable: false,
    },
    {
      field: "delete",
      headerName: "",
      width: 60,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            safelyDeleteRow(params.row.id);
          }}
          aria-label="delete"
          color="error"
        >
          <CloseIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ position: "relative", mb: 2 }}>
        <TextField
          fullWidth
          id="search-cabinets"
          type="text"
          variant="outlined"
          placeholder="Search by Product Name or Product Description..."
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          value={searchTerm}
          inputRef={searchInputRef}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "black",
              },
              "&:hover fieldset": {
                borderColor: "black",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#d32f2f",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} aria-label="clear">
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {searchTerm.trim() === "" && (
          <Typography variant="body2" sx={{ color: "red", mt: 1, ml: 0.5 }}>
            Tip: Use filters before searching to narrow your results.
          </Typography>
        )}

        {dropdownOpen && (
          <div ref={dropdownRef}>
            <SelectedItemList
              items={rows}
              searchTerm={searchTerm}
              selectedColors={selectedColors}
              selectedLines={selectedLines}
              multiplier={multiplier}
              onAddToVisibleRows={handleAddToVisibleRows}
              visibleRows={visibleRows}
              handleQuantityChange={(
                index,
                quantity,
                assemblySelected,
                hingeDirection
              ) => {
                const updated = [...rows];
                updated[index].quantity = quantity;
                updated[index].assemblySelected = assemblySelected;
                updated[index].hingeDirection = hingeDirection;
                setFetchedData(updated);
              }}
            />
          </div>
        )}
      </Box>

      <ThemeProvider theme={theme}>
        <Box
          sx={{
            height: isPhone ? "80vh" : "80vh",
            width: "100%",
          }}
        >
          <DataGrid
            rows={dataGridRows}
            columns={columns}
            pageSizeOptions={[5, 10, 15]}
            autoPageSize={isPhone}
            pagination={!isPhone}
            sx={{
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
            }}
            disableColumnMenu
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            hideFooter={isMediumOrAbove}
            slots={{
              noRowsOverlay: () => (
                <CustomNoRowsOverlay
                  onImportClick={() => fileInputRef?.current?.click()}
                />
              ),
            }}
          />
        </Box>
      </ThemeProvider>
    </>
  );
}

export default ItemList;
